
import Vue, { PropType, toRaw } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import Api from '@/functions/api';
import Format from '@/functions/format';
import tracking from '@/functions/tracking';
import { EventBus } from '@/functions/eventBus';

import KeyValue from '@/interfaces/keyValue.interface';
import { ISearchAgent } from '@/interfaces/searchAgent.interface';
import SearchAgentLead from '@/interfaces/searchAgentLead.interface';

import mTag from '@/components/molecules/form/Tag.vue';
import Filter from '@/interfaces/filter.interface';
import { FILTER_NAMES, SEARCH_ENERGY_LABELS } from '@/constants/search.const';
import LeadConsent from '@/interfaces/leadConsent.interface';
import { MINDWORKING_REDIRECT_TYPES } from '@/constants/mindworkingRedirectTypes.const';

const defaultTrackingText = 'Logget ind paa forhaand' as const;

interface IData {
    currentDirection: string;
    currentGroup: string | null;
    isLoading: boolean;
    isDesktop: boolean;
    invalidText: string;
    form: {
        name: string;
    };
    errors: {
        name?: string;
    };
    priceComplexInterval: number[];
    trackingEntrance: string;
    loginUrl: string;
}
export default {
    components: {
        mTag,
    },

    props: {
        redirected: {
            type: Boolean as PropType<boolean>,
            required: true,
            default: () => false,
        },

        redirectedFrom: {
            type: String as PropType<string>,
            required: true,
            default: () => '',
        },

        searchAgent: {
            type: Object as PropType<ISearchAgent>,
            default: () => ({
                isLoggedIn: false,
                button: {
                    text: '',
                    icon: '',
                },
                consent: {
                    type: Object as PropType<LeadConsent>,
                    default: () => ({
                        id: '',
                        heading: '',
                        purposeText: '',
                    }),
                },
                tooltip: {
                    headline: '',
                    content: '',
                    illegalFiltersText: '',
                    close: '',
                },
                stepOne: {
                    headline: '',
                    content: '',
                    nameLabel: '',
                    namePlaceholder: '',
                    saveButtonText: '',
                    signupLoginButtonText: '',
                },
                // stepTwo id login at Mindworking
                stepThree: {
                    headline: '',
                    content: '',
                    profileLinkUrl: '',
                    profileLinkText: '',
                },
            }),
        },
    },

    data(): IData {
        return {
            currentDirection: '',
            currentGroup: null,
            isLoading: false,
            isDesktop: EventBus.isDesktop,
            invalidText: '',
            form: {
                name: '',
            },
            priceComplexInterval: [],
            errors: {},
            trackingEntrance: defaultTrackingText,
            loginUrl: '',
        };
    },

    computed: {
        ...mapGetters({
            area: 'search/area',
            filters: 'search/filters',
            isDrawingArea: 'search/isDrawingArea',
            query: 'search/query',

            currentPanel: 'searchAgent/currentPanel',
            agentName: 'searchAgent/agentName',

            bounds: 'search/bounds',

            modalShow: 'modal/show',
            modalType: 'modal/type',
        }),

        polygon(): number[][] {
            return this.area && this.area.length ? this.area : this.bounds;
        },

        showSearchAgentOverlay(): boolean {
            return this.modalShow && this.modalType === 'searchAgent';
        },

        hasInvalidFilters(): boolean {
            const selectedFilters = JSON.parse(JSON.stringify(this.filters));
            this.invalidText = '';

            if (
                !selectedFilters.some(
                    (e) => e.key.toLowerCase() === 'propertytype',
                )
            ) {
                return true;
            }

            if (
                // Kolinihave
                selectedFilters.some(
                    (e) =>
                        e.key.toLowerCase() === 'propertytype' &&
                        e.value === '401',
                ) ||
                selectedFilters.some(
                    (e) =>
                        e.key.toLowerCase() === 'propertyform' &&
                        e.value === 'lejebolig',
                ) ||
                selectedFilters.some(
                    (e) =>
                        e.key.toLowerCase() === 'propertyform' &&
                        e.value.toLowerCase() === 'flexproperty',
                ) ||
                selectedFilters.some((e) => e.key.toLowerCase() === 'brutto') ||
                selectedFilters.some((e) => e.key.toLowerCase() === 'netto')
            ) {
                this.invalidText = this.searchAgent.tooltip.illegalFiltersText;
                return true;
            }

            return false;
        },
    },

    watch: {
        form: {
            deep: true,
            handler() {
                this.errors = {};
            },
        },
        filters: {
            deep: true,
            handler() {
                this.closeTooltip();
            },
        },
        area: {
            handler() {
                this.closeTooltip();
            },
        },
    },

    mounted(): void {
        EventBus.$on('app.resize', () => {
            this.isDesktop = EventBus.isDesktop;
        });

        if (this.currentPanel === 'searchAgentNameSuccess') {
            this.setCurrentPanel('');
            this.reset();
        }

        if (this.agentName === '' && !this.redirected) {
            this.setName(this.searchAgent.stepOne.namePlaceholder);
        }

        this.form.name = this.agentName;

        if (
            this.redirected &&
            this.redirectedFrom === MINDWORKING_REDIRECT_TYPES.MW_SEARCH_AGENT
        ) {
            this.trackingEntrance = 'Login/Opret bruger i MW';
            // this.open();
            this.$store.dispatch('modal/open', 'searchAgent');
            Vue.nextTick(() => {
                this.currentDirection = 'next';
                this.setCurrentPanel('searchAgentNameSuccess');
                // we cannot submit until the polygon is ready,
                // this is not always the case if we are coming
                // back from Mindworking after login
                const interval = 100; // Retry every 100 milliseconds
                const timeout = 10000; // Timeout after 10 seconds
                let elapsedTime = 0;

                const intervalId = setInterval(() => {
                    if (this.polygon.length > 0) {
                        clearInterval(intervalId);

                        this.onSubmit(true);
                    } else {
                        elapsedTime += interval;
                        if (elapsedTime >= timeout) {
                            clearInterval(intervalId);
                            throw new Error('Operation timed out');
                        }
                    }
                }, interval);
            });
        }
    },

    methods: {
        ...mapActions({
            reset: 'searchAgent/reset',
            setName: 'searchAgent/setName',
            setCurrentPanel: 'searchAgent/setCurrentPanel',
            open: 'searchAgent/open',
        }),

        /**
         * Update a input filter.
         *
         * @param {Filter} filter
         * @param {string} value
         * @return {void}
         */
        updateInput(name: string, value: string): void {
            if (name === 'name') {
                this.setName(value);
            }
        },

        capitalize(word: string): string {
            return Format.capitalize(word);
        },

        closeTooltip(): void {
            const searhAgentTooltip = this.$refs
                .searhAgentTooltip as HTMLDivElement;
            searhAgentTooltip?.classList.remove('open');
        },

        openTooltip(): void {
            const searhAgentTooltip = this.$refs
                .searhAgentTooltip as HTMLDivElement;
            searhAgentTooltip?.classList.add('open');
        },

        onClick(e: MouseEvent | TouchEvent): void {
            this.errors = {};
            this.setCurrentPanel('');
            this.openTooltip();

            const currentSearch = window.location.search;
            const encoded = encodeURIComponent(currentSearch);

            const base =
                '/login/?redirectUrl=%2Fsoeg%2F%3F' +
                encoded +
                '%26redirect%3D' +
                MINDWORKING_REDIRECT_TYPES.MW_SEARCH_AGENT;

            this.loginUrl = base;

            if (this.hasInvalidFilters) {
                const searhAgentTooltip = this.$refs
                    .searhAgentTooltip as HTMLDivElement;
                searhAgentTooltip?.classList.add('open');
                this.trackInvalidFilter();
                return;
            }

            this.open(this.currentPanel);
            tracking.trackRawEvent({
                event: 'trackForm',
                eventData: {
                    category: 'Formular',
                    action: 'Soegeagent initieret',
                    label: 'Soegeagent',
                    formularIndgang: 'CTA klik',
                },
            });
        },

        trackInvalidFilter(): void {
            const selectedFilters = JSON.parse(JSON.stringify(this.filters));

            if (
                !selectedFilters.some(
                    (e) => e.key.toLowerCase() === 'propertytype',
                )
            ) {
                tracking.trackRawEvent({
                    event: 'trackForm',
                    eventData: {
                        category: 'Formular',
                        action: 'Soegeagent initieret inaktiv',
                        label: 'Soegeagent',
                        formularIndgang: 'Manglende boligtype',
                    },
                });
                return;
            }

            let invalidFilters: string[] = [];

            if (
                // Kolinihave
                selectedFilters.some(
                    (e) =>
                        e.key.toLowerCase() === 'propertytype' &&
                        e.value === '401',
                )
            ) {
                invalidFilters.push('Kolonihave');
            }
            if (
                selectedFilters.some(
                    (e) =>
                        e.key.toLowerCase() === 'propertyform' &&
                        e.value === 'lejebolig',
                )
            ) {
                invalidFilters.push('Lejebolig');
            }

            if (
                selectedFilters.some(
                    (e) =>
                        e.key.toLowerCase() === 'propertyform' &&
                        e.value === 'FlexProperty',
                )
            ) {
                invalidFilters.push('Flexbolig');
            }

            if (selectedFilters.some((e) => e.key.toLowerCase() === 'netto')) {
                invalidFilters.push('Netto pris');
            }

            if (selectedFilters.some((e) => e.key.toLowerCase() === 'brutto')) {
                invalidFilters.push('Brutto pris');
            }

            invalidFilters.length > 0
                ? tracking.trackRawEvent({
                      event: 'trackForm',
                      eventData: {
                          category: 'Formular',
                          action: 'Soegeagent initieret inaktiv',
                          label: 'Soegeagent',
                          formularIndgang: `Invalide filtre valgt: ${invalidFilters.join(' + ')}`,
                      },
                  })
                : null;
            return;
        },

        getSliderFilterValues(filterName: string): number[] {
            const selectedFilters: Filter[] = toRaw(this.filters);
            const filter = selectedFilters.filter(
                (t) => t.key === filterName,
            )[0];

            return [parseInt(filter.value[0]), parseInt(filter.value[1])];
        },

        generateLeadRequest(): SearchAgentLead {
            const selectedFilters: Filter[] = toRaw(this.filters);

            const searchAgentLead: SearchAgentLead = {
                propertyTypes: [],
                zipCodes: '',
                name: this.agentName,
            };

            //#region Boligen
            // --------------
            // BOLIGEN FILTRE
            // --------------

            //PropertyType Filters
            if (
                selectedFilters.some(
                    (t: Filter) => t.key === FILTER_NAMES.PropertyType,
                )
            ) {
                const propertyTypeFilters = selectedFilters.filter(
                    (t: Filter) => t.key === FILTER_NAMES.PropertyType,
                );
                let propertyTypes =
                    propertyTypeFilters?.map((t: Filter) => t.label) || [];

                searchAgentLead.propertyTypes = propertyTypes;
            }

            // Built Year filter
            if (
                selectedFilters.some(
                    (t: Filter) => t.key === FILTER_NAMES.BuiltYear,
                )
            ) {
                const values = [...this.getSliderFilterValues('BuiltYear')];
                searchAgentLead.yearBuiltFrom = values[0];
                searchAgentLead.yearBuiltTo = values[1];
            }
            // Energimærke filter
            if (
                selectedFilters.some(
                    (t: Filter) => t.key === FILTER_NAMES.EnergyLabel,
                )
            ) {
                const values = [...this.getSliderFilterValues('EnergyLabel')];
                const startValue = values[0];
                const endValue = values[1];

                const labels = Object.keys(SEARCH_ENERGY_LABELS)
                    .map(Number)
                    .filter((key) => key >= startValue && key <= endValue)
                    .map((key) => SEARCH_ENERGY_LABELS[key]);

                // Add missing "A" labels that exists in Mindworking, but not in the filters
                if (labels.includes('A')) {
                    labels.push('A1', 'A2', 'A2010', 'A2015', 'A2020');
                }

                searchAgentLead.searchProfileEnergyMarkSeverities = [...labels];
            }
            //#endregion

            //#region Belliggenhed
            // --------------------
            // BELLIGGENHEDS FILTRE
            // --------------------

            // Afstand til vand
            if (
                selectedFilters.some(
                    (t: Filter) => t.key === FILTER_NAMES.DistanceToWater,
                )
            ) {
                const values = [
                    ...this.getSliderFilterValues('DistanceToWater'),
                ];
                searchAgentLead.distanceCoastFrom = values[0];
                searchAgentLead.distanceCoastTo = values[1];
            }

            // Afstand til skole
            if (
                selectedFilters.some(
                    (t) => t.key === FILTER_NAMES.DistanceToSchool,
                )
            ) {
                const values = [
                    ...this.getSliderFilterValues('DistanceToSchool'),
                ];
                searchAgentLead.distanceSchoolFrom = values[0];
                searchAgentLead.distanceSchoolTo = values[1];
            }

            // Afstand til indkøb
            if (
                selectedFilters.some(
                    (t: Filter) => t.key === FILTER_NAMES.DistanceToShopping,
                )
            ) {
                const values = [
                    ...this.getSliderFilterValues('DistanceToShopping'),
                ];
                searchAgentLead.distanceSupermarketFrom = values[0];
                searchAgentLead.distanceSupermarketTo = values[1];
            }

            // Afstand til daginstitution
            if (
                selectedFilters.some(
                    (t) => t.key === FILTER_NAMES.DistanceToDaycare,
                )
            ) {
                const values = [
                    ...this.getSliderFilterValues('DistanceToDaycare'),
                ];
                searchAgentLead.distanceDaycareFrom = values[0];
                searchAgentLead.distanceDaycareTo = values[1];
            }

            // Afstand til offentlig transport
            if (
                selectedFilters.some(
                    (t) => t.key === FILTER_NAMES.DistanceToPublicTransport,
                )
            ) {
                const values = [
                    ...this.getSliderFilterValues('DistanceToPublicTransport'),
                ];
                searchAgentLead.distancePublicTransportationFrom = values[0];
                searchAgentLead.distancePublicTransportationTo = values[1];
            }
            //#endregion

            //#region pris
            // -----------
            // PRIS FILTRE
            // -----------

            // Price filter
            if (selectedFilters.some((t) => t.key === FILTER_NAMES.Price)) {
                const values = [...this.getSliderFilterValues('Price')];
                searchAgentLead.priceFrom = values[0];
                searchAgentLead.priceTo = values[1];
            }
            //#endregion

            //#region Størrelse
            // ----------------
            // STØRRELSE FILTRE
            // ----------------

            // Bolig størrelse
            if (
                selectedFilters.some((t) => t.key === FILTER_NAMES.Propertysize)
            ) {
                const values = [...this.getSliderFilterValues('Propertysize')];
                searchAgentLead.houseAreaFrom = values[0];
                searchAgentLead.houseAreaTo = values[1];
            }
            if (
                selectedFilters.some((t) => t.key === FILTER_NAMES.Groundsize)
            ) {
                const values = [...this.getSliderFilterValues('Groundsize')];
                searchAgentLead.plotAreaFrom = values[0];
                searchAgentLead.plotAreaTo = values[1];
            }
            if (selectedFilters.some((t) => t.key === FILTER_NAMES.Rooms)) {
                const values = [...this.getSliderFilterValues('Rooms')];
                searchAgentLead.roomCountFrom = values[0];
                searchAgentLead.roomCountTo = values[1];
            }
            if (selectedFilters.some((t) => t.key === FILTER_NAMES.Plan)) {
                const values = [...this.getSliderFilterValues('Plan')];
                searchAgentLead.floorCountFrom = values[0];
                searchAgentLead.floorCountTo = values[1];
            }
            if (selectedFilters.some((t) => t.key === FILTER_NAMES.Floor)) {
                const values = [...this.getSliderFilterValues('Floor')];
                searchAgentLead.floorNumberFrom = values[0];
                searchAgentLead.floorNumberTo = values[1];
            }
            //#endregion

            //#region Faciliteter
            if (
                selectedFilters.some(
                    (t) => t.key === FILTER_NAMES.Facilities,
                ) ||
                selectedFilters.some(
                    (t: Filter) => t.key === FILTER_NAMES.PropertyForm,
                )
            ) {
                const facilityFilters = selectedFilters.filter(
                    (t: Filter) =>
                        t.key === FILTER_NAMES.Facilities ||
                        t.key === FILTER_NAMES.PropertyForm,
                );
                let facilities =
                    facilityFilters?.map((t: KeyValue) => t.value) || [];

                searchAgentLead.searchProfileWords = facilities;
            }

            //#endregion

            searchAgentLead.zipCodes = this.query ?? '';

            searchAgentLead.polygon = Format.searchAgentPolygonHelper(
                this.polygon,
            );

            return searchAgentLead;
        },

        onLogin(): void {
            tracking.trackRawEvent({
                event: 'trackForm',
                eventData: {
                    category: 'Formular',
                    action: `Soegeagent indsend - ${this.searchAgent.stepOne.signupLoginButtonText}`,
                    label: 'Soegeagent',
                    formularIndgang: 'CTA klik',
                    formularStepnavn: this.searchAgent.stepOne.headline,
                },
            });
        },

        onSubmit(redirected: boolean): void {
            this.isLoading = true;

            if (!this.errors.name && !redirected) {
                tracking.trackRawEvent({
                    event: 'trackForm',
                    eventData: {
                        category: 'Formular',
                        action: `Soegeagent indsend - ${this.searchAgent.stepOne.saveButtonText}`,
                        label: 'Soegeagent',
                        formularIndgang: 'CTA click',
                        formularStepnavn: this.searchAgent.stepOne.headline,
                    },
                });
            }

            this.errors = {};

            const searchAgentLead: SearchAgentLead = this.generateLeadRequest();

            Api.postBuyerDirectoryLead(searchAgentLead)
                .then(() => {
                    this.currentDirection = 'next';
                    this.setCurrentPanel('searchAgentNameSuccess');
                })
                .catch((error: any) => {
                    this.errors = error.errors ?? {};
                })
                .finally(() => {
                    this.isLoading = false;

                    if (!this.errors.name) {
                        tracking.trackRawEvent({
                            event: 'trackForm',
                            eventData: {
                                category: 'Formular',
                                action: 'Soegeagent gennemfoert',
                                label: 'Soegeagent',
                                formularIndgang: this.trackingEntrance,
                                formularStepnavn: 'Soegeagent oprettet',
                            },
                        });
                        this.trackingEntrance = defaultTrackingText;
                    }
                });
        },
    },
};
